import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { hexToHslParts, readableForeground } from "../lib/format";
import { LayoutDashboard, CalendarDays, Users, Scissors, UserCog, Bell, CreditCard, Settings as SettingsIcon, LogOut, ExternalLink, AlertTriangle, ClipboardList } from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["owner", "receptionist", "stylist"] },
  { to: "/appointments", label: "Appointments", icon: CalendarDays, roles: ["owner", "receptionist", "stylist"] },
  { to: "/clients", label: "Clients", icon: Users, roles: ["owner", "receptionist", "stylist"] },
  { to: "/waitlist", label: "Waitlist", icon: ClipboardList, roles: ["owner", "receptionist", "stylist"] },
  { to: "/services", label: "Services", icon: Scissors, roles: ["owner", "receptionist"] },
  { to: "/team", label: "Team", icon: UserCog, roles: ["owner"] },
  { to: "/messages", label: "Messages", icon: Bell, roles: ["owner", "receptionist", "stylist"] },
  { to: "/billing", label: "Billing", icon: CreditCard, roles: ["owner"] },
  { to: "/settings", label: "Settings", icon: SettingsIcon, roles: ["owner"] },
];

function trialDaysLeft(salon) {
  const te = salon?.subscription?.trial_end;
  if (!te) return null;
  const ms = new Date(te) - new Date();
  return Math.max(0, Math.ceil(ms / 86400000));
}

export default function CrmLayout({ title, actions, greeting, stats, children }) {
  const { user, salon, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const items = NAV.filter((n) => n.roles.includes(user?.role));
  const sub = salon?.subscription || {};
  const trialing = sub.status === "trialing";
  const days = trialDaysLeft(salon);
  const noAccess = !salon?.has_access;
  const customHeader = salon?.settings?.branding?.dashboard_header;
  const accentParts = hexToHslParts(salon?.settings?.brand_color);
  const brandStyle = accentParts
    ? { "--accent": accentParts, "--ring": accentParts, "--accent-foreground": readableForeground(salon?.settings?.brand_color) }
    : undefined;

  return (
    <div className="min-h-screen flex bg-secondary/40 text-foreground" style={brandStyle}>
      <aside className="w-60 shrink-0 bg-card text-foreground border-r border-border/60 flex flex-col fixed h-screen">
        <div className="h-16 flex items-center px-6 border-b border-border">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img src="/bookingbabe-logo.png" alt="Booking Babe" className="w-7 h-7 rounded-md object-cover" />
            <span className="font-display text-xl font-semibold tracking-tight text-foreground">Booking Babe</span>
          </Link>
        </div>
        <div className="px-6 py-3 border-b border-border">
          <div className="text-xs text-muted-foreground uppercase tracking-wider">Salon</div>
          <div className="text-sm text-foreground/90 truncate">{salon?.name}</div>
        </div>
        <nav className="flex-1 py-3 px-3 space-y-0.5 overflow-y-auto">
          {items.map((n) => {
            const Icon = n.icon;
            const active = location.pathname === n.to || location.pathname.startsWith(n.to + "/");
            return (
              <Link key={n.to} to={n.to} data-testid={`sidebar-${n.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${active ? "bg-accent/15 text-foreground font-medium border-l-2 border-accent" : "text-muted-foreground hover:text-foreground hover:bg-secondary"}`}>
                <Icon size={17} strokeWidth={1.5} aria-hidden="true" /> {n.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="px-3 py-1.5 text-xs">
            <div className="text-foreground/90 truncate">{user?.name}</div>
            <div className="text-muted-foreground capitalize">{user?.role}</div>
          </div>
          <button data-testid="crm-logout" onClick={() => { logout(); navigate("/"); }}
            className="flex items-center gap-3 w-full px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
            <LogOut size={17} strokeWidth={1.5} aria-hidden="true" /> Log out
          </button>
        </div>
      </aside>

      <main className="flex-1 ml-60 min-w-0">
        <header
          className="relative h-44 border-b border-border overflow-hidden flex items-end"
          data-testid="crm-header"
        >
          {customHeader ? (
            <>
              <div
                className="absolute inset-0"
                style={{ backgroundImage: `url('${customHeader}')`, backgroundSize: "cover", backgroundPosition: "center" }}
              />
              <div
                className="absolute inset-0"
                style={{ background: "linear-gradient(90deg, rgba(43,30,22,0.88) 0%, rgba(43,30,22,0.55) 55%, rgba(43,30,22,0.35) 100%)" }}
              />
            </>
          ) : (
            <div
              className="absolute inset-0"
              style={{
                backgroundColor: "rgb(43,30,22)",
                backgroundImage: "radial-gradient(rgba(150,110,80,0.16) 2px, transparent 2.5px)",
                backgroundSize: "26px 26px",
                backgroundPosition: "0 0",
              }}
            />
          )}
          <div className="relative z-10 flex items-end justify-between w-full px-8 pb-6 gap-4">
            <div className="min-w-0">
              <div className="label-eyebrow text-white/70 truncate">{greeting || salon?.name}</div>
              <h1 className="font-display text-4xl md:text-5xl font-semibold text-white drop-shadow-md truncate">{title}</h1>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {stats && stats.length > 0 && (
                <div className="hidden md:flex items-center gap-2" data-testid="header-quick-stats">
                  {stats.map((s) => (
                    <div key={s.label} className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/15 px-4 py-2 text-right" data-testid={`header-stat-${s.label}`}>
                      <div className="text-[10px] uppercase tracking-wider text-white/60 whitespace-nowrap">{s.label}</div>
                      <div className="font-display text-xl font-semibold text-white leading-tight">{s.value}</div>
                    </div>
                  ))}
                </div>
              )}
              {actions}
            </div>
          </div>
        </header>

        {trialing && salon?.has_access && (
          <div className="bg-accent/15 border-b border-accent/30 px-8 py-2.5 flex items-center justify-between text-sm">
            <span className="text-foreground">You're on a free trial — <strong>{days} day{days === 1 ? "" : "s"}</strong> left. Subscribe to keep your salon running.</span>
            <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 h-7" data-testid="trial-upgrade" onClick={() => navigate("/billing")}>Choose a plan</Button>
          </div>
        )}

        {noAccess ? (
          <div className="p-8">
            <div className="max-w-lg mx-auto bg-card border border-border rounded-lg p-10 text-center mt-10">
              <div className="w-12 h-12 rounded-full bg-destructive/10 text-destructive flex items-center justify-center mx-auto mb-4"><AlertTriangle size={24} /></div>
              <h2 className="font-display text-2xl font-semibold mb-2">Your trial has ended</h2>
              <p className="text-muted-foreground mb-6">Subscribe to a plan to reactivate your salon's CRM, booking page and reminders.</p>
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="reactivate-btn" onClick={() => navigate("/billing")}>View plans</Button>
            </div>
          </div>
        ) : (
          <div className="p-8">{children}</div>
        )}

        {salon?.slug && salon?.has_access && (
          <a href={`/book/${salon.slug}`} target="_blank" rel="noreferrer" data-testid="view-booking-page"
            className="fixed bottom-7 right-7 bg-accent text-accent-foreground text-base font-semibold px-7 py-4 rounded-full shadow-xl hover:bg-accent/90 hover:scale-105 transition-transform duration-200 flex items-center gap-2.5 z-40">
            <ExternalLink size={20} /> View booking site
          </a>
        )}
      </main>
    </div>
  );
}
